import React from 'react';
import { Card, Dropdown, Button, Typography, Tag, Space, Descriptions } from 'antd';
import type { MenuProps } from 'antd';
import {
  EllipsisOutlined,
  PlayCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  FunctionOutlined, // A fitting icon for a model
} from '@ant-design/icons';
import { GalleryItem } from './CardGallery';

// Assuming ModelRowProps has these properties. Adjust as needed.
export interface ModelRowProps extends GalleryItem{
  model_name: string;
  model_type: string;
  dataset: string;
  recall?: number; // Optional, if not always available
  precision?: number; // Optional, if not always available
  created_at: string | Date;
  latest_version?: string;
  status: 'Training' | 'Deployed' | 'Archived' | 'Ready'; // Example statuses
  description?: string;
  thumbnailUrl?: string;
}

interface ModelCardProps {
  item: ModelRowProps;
  onAction?: (actionKey: string, item: ModelRowProps) => void;
  showActions?: boolean;
}

// Helper function to determine tag color based on status
const getStatusColor = (status: ModelRowProps['status']) => {
  switch (status) {
    case 'Deployed': return 'green';
    case 'Training': return 'blue';
    case 'Archived': return 'gold';
    case 'Ready': return 'cyan';
    default: return 'default';
  }
};

export const ModelCard: React.FC<ModelCardProps> = ({ item, onAction, showActions = true }) => {
  const statusColor = getStatusColor(item.status);

  // Define actions for the dropdown menu
  const menuItems: MenuProps['items'] = [
    { key: 'deploy', icon: <PlayCircleOutlined />, label: 'Deploy' },
    { key: 'rename', icon: <EditOutlined />, label: 'Rename' },
    { type: 'divider' },
    // { key: 'delete', icon: <DeleteOutlined />, label: 'Delete', danger: true },
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key, domEvent }) => {
    // Stop propagation to prevent the card's main click event
    domEvent.stopPropagation();
    domEvent.preventDefault();
    onAction?.(key, item);
  };

    

  return (
    <Card
      hoverable
      className="model-card overflow-hidden rounded-lg shadow-md transition-all w-full hover:shadow-lg relative"
      styles={{ body: { padding: '16px', height: '100%' } }}
      style={{ borderTop: `2px solid transparent`, transition: 'all 0.3s ease' }}
    >
      {/* Kebab menu for actions */}
      {showActions && (
        <div className="absolute top-2 right-2 z-10">
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            menu={{ items: menuItems, onClick: handleMenuClick }}
          >
            <Button
              type="text"
              shape="circle"
              aria-label="More actions"
              icon={<EllipsisOutlined />}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
            />
          </Dropdown>
        </div>
      )}

      <div className="flex flex-col h-full">
        {/* Thumbnail or Placeholder Icon */}
        <div className="h-32 flex items-center justify-center mb-4 bg-gray-50 rounded p-2">
          {item.thumbnailUrl ? (
            <img
              src={item.thumbnailUrl}
              alt={`${item.model_name} thumbnail`}
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <FunctionOutlined style={{ fontSize: '48px', opacity: 0.5 }} />
          )}
        </div>

        {/* Title + Status */}
        <div className="mb-4 flex justify-between items-center pr-8">
          <Typography.Title level={5} className="m-0" ellipsis={{ tooltip: item.model_name }}>
            {item.model_name}
          </Typography.Title>
          <Tag color={statusColor}>{item.status}</Tag>
        </div>

        {/* Key Info */}
        <Space direction="vertical" size="small" className="mb-2 w-full">
          <div className="flex justify-between">
            <Typography.Text type="secondary">Type:</Typography.Text>
            <Typography.Text strong>{item.model_type}</Typography.Text>
          </div>
          <div className="flex justify-between">
            <Typography.Text type="secondary">Dataset:</Typography.Text>
            <Typography.Text strong ellipsis={{ tooltip: item.dataset }}>
              {item.dataset}
            </Typography.Text>
          </div>
        </Space>

        {/* Description */}
        <div className="mb-4">
          <Typography.Paragraph
            type="secondary"
            ellipsis={{ rows: 2, expandable: false, tooltip: item.description }}
          >
            {item.description || 'No description provided'}
          </Typography.Paragraph>
        </div>

        {/* Details using Descriptions component */}
        <Descriptions
          size="small"
          column={1}
          bordered
          className="mt-auto" // Pushes details to the bottom
          styles={{ content: { textAlign: 'right' } }}
        >
          <Descriptions.Item label="Recall">
            {typeof item.recall === 'number' ? `${(item.recall * 100).toFixed(2)}%` : item.recall}
          </Descriptions.Item>
          <Descriptions.Item label="Precision">
            {typeof item.precision === 'number' ? `${(item.precision * 100).toFixed(2)}%` : item.precision}
          </Descriptions.Item>
          <Descriptions.Item label="Version">
            {item.latest_version || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Created">
            {new Intl.DateTimeFormat('en-US').format(new Date(item.created_at))}
          </Descriptions.Item>
        </Descriptions>
      </div>
    </Card>
  );
};

export default ModelCard;